import {
  COMPANY_QUARTER,
  type CollisionGrid,
  exitAt,
  type MapIcon,
  MOBS,
  type QuestMarker,
  PROPS,
  type TerrainField,
  terrainOf,
  type ZoneDefinition,
} from '@shefira/shared';

import { followPreference } from '../settings/preferences.js';
import { minimapLayout, readMinimapLarge, saveMinimapLarge } from './minimap-size.js';
import { paintPixels } from './pixel-canvas.js';
import { uiFormat, uiText } from './text.js';
import { isTouch, onTouchChange } from './touch.js';

/**
 * The minimap, top left: the land around the player at one pixel a tile,
 * each terrain in its colour, woods where props block, villages as small
 * squares, the player in white, other players in blue, friends in green,
 * both rimmed and drawn over the pictograms and the portals' arches
 * (ADR 0033), the mobs in sight in red. Its title says where the player stands: a village, or the biome.
 * A click enlarges it, two pixels a tile, and back (SHE-37).
 *
 * Display only. It draws the zone's own data (terrain, collision, props,
 * landmarks) and the positions the server already synchronises; it knows
 * no more than the screen does. Mobs outside the area of interest are not
 * in the state, so they are not on the map either — it is not a radar.
 */

export interface MinimapDot {
  x: number;
  y: number;
  /**
   * `friend`: a friend in the zone, from `friend_positions`, seen even out of sight.
   * `quest`: where a quest under way leads (ADR 0047), held to the edge when out of the window.
   * `event`: the village a world event falls on (ADR 0050), held to the edge the same way.
   * `encounter`: a road encounter (ADR 0093), only when in the window, never held to the edge.
   * `rare`: a wandering rare (ADR 0094), a mob in the state like any other, so
   * only ever near the player; never held to the edge, never further.
   */
  kind: 'self' | 'player' | 'friend' | 'mob' | 'rare' | 'quest' | 'event' | 'encounter';
  /** An encounter with nothing more for this player: drawn faded. */
  faded?: boolean;
}

export interface MinimapOptions {
  zone: ZoneDefinition;
  /** Collision: what cannot be walked and is not a terrain is woods. */
  grid: CollisionGrid;
  /** The ground, terrain by terrain. */
  field: TerrainField;
}

const COLOURS = {
  woods: '#23421f',
  rock: '#8c8c94',
  house: '#b0703c',
  exit: '#f0c850',
  self: '#ffffff',
  player: '#6fb8ff',
  friend: '#9ece6a',
  mob: '#ff5c5c',
  quest: '#ffc82e',
  event: '#ff5c3c',
} as const;

/**
 * The pictograms of the townsfolk worth finding (`mapIcon` in the mob data):
 * seven pixels of shape, `#` the body and `o` its light, drawn on whole pixels
 * with a one-pixel dark rim so they read on any ground. Drawn at their spawn,
 * from the zone's data like the villages: they show even out of sight.
 */
const PICTOGRAMS: Record<MapIcon, { rows: readonly string[]; body: string; light: string }> = {
  anvil: {
    rows: ['oo.....', '#######', '.#####.', '...#...', '..###..', '.#####.'],
    body: '#8c8c9c',
    light: '#e6e6f0',
  },
  coins: {
    rows: ['..###..', '.#ooo#.', '#o###o#', '#o#o#o#', '#o###o#', '.#ooo#.', '..###..'],
    body: '#c88a1c',
    light: '#ffc82e',
  },
  crucible: {
    rows: ['#.....#', '#######', '#ooooo#', '.#ooo#.', '.#####.', '..###..'],
    body: '#6b6b78',
    light: '#f07a1c',
  },
  flask: {
    rows: ['..###..', '..#o#..', '.#ooo#.', '#ooooo#', '#ooooo#', '.#####.'],
    body: '#6b6b78',
    light: '#6fe07a',
  },
  scales: {
    rows: ['...o...', '#######', '#..#..#', 'oo.#.oo', '...#...', '..###..'],
    body: '#c88a1c',
    light: '#fff1a8',
  },
  // The stable master's horseshoe (ADR 0047), iron like the smith's anvil.
  horseshoe: {
    rows: ['#.....#', '#o...o#', '#o...o#', '#o...o#', '.#o.o#.', '..###..'],
    body: '#8c8c9c',
    light: '#e6e6f0',
  },
  // The golden « ! » over a townsperson who offers a quest (ADR 0047): drawn
  // only while they do (`draw`'s markers), never baked into the map.
  quest: {
    rows: ['..###..', '..#o#..', '..#o#..', '..#o#..', '..###..', '.......', '..#o#..'],
    body: '#c88a1c',
    light: '#ffc82e',
  },
};
const PICTOGRAM_RIM = '#12121c';
/** The « ? » over a townsperson (ADR 0047): golden to turn a quest in or hear a word, grey while one is under way. */
const QUESTION_ROWS = ['.###.', '#ooo#', '...o#', '..o#.', '..#..', '.....', '..o..'];
const QUEST_PICTOGRAMS: Record<
  QuestMarker,
  { rows: readonly string[]; body: string; light: string }
> = {
  available: PICTOGRAMS.quest,
  ready: { rows: QUESTION_ROWS, body: '#c88a1c', light: '#ffc82e' },
  pending: { rows: QUESTION_ROWS, body: '#4a4a58', light: '#b0b0c0' },
};

/** Pixels a quest mark floats over its townsperson's tile. */
const MARKER_LIFT = 6;

/** A townsperson's quest marker on the map, where they stand. */
export interface MinimapMarker {
  x: number;
  y: number;
  marker: QuestMarker;
}
/**
 * A boss's dungeon (ADR 0027): its keep, drawn at its gate in the red of
 * the world map's lair diamond, only out in the valley (inside, the whole
 * zone is the dungeon).
 */
const LAIR_PICTOGRAM = {
  rows: ['#.#.#.#', '#######', '#o#o#o#', '#######', '###.###', '##...##'],
  body: '#b03028',
  light: '#ffd0c8',
};

/**
 * The Givrecoeur Company's bastion at Havrebourg (ADR 0067), the heart of the
 * story: its snowflake in frost blue and white, at the middle of its yard.
 */
const COMPANY_PICTOGRAM = {
  rows: ['#..#..#', '.#.#.#.', '..###..', '###o###', '..###..', '.#.#.#.', '#..#..#'],
  body: '#66afd7',
  light: '#f8f2ea',
};

export class Minimap {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D | null;
  /**
   * The dots, on a canvas of their own over the portals' arches: a village's
   * arch covers sixteen tiles of its square, right where players gather.
   */
  private readonly dots: HTMLCanvasElement;
  private readonly dotsContext: CanvasRenderingContext2D | null;
  /**
   * The whole zone at one pixel a tile, drawn once; each redraw copies the
   * window, then adds the dots. The world map shows it whole.
   */
  readonly base: HTMLCanvasElement;
  private view = 0;
  private large = readMinimapLarge();
  private last: { dots: readonly MinimapDot[]; markers: readonly MinimapMarker[] } = {
    dots: [],
    markers: [],
  };
  private readonly unfollow: () => void;
  private readonly offTouch: () => void;
  /**
   * The villages' portals (ADR 0017), in the favicon's arch: a 32-pixel
   * drawing no tile-sized pictogram could hold, so it rides over the canvas
   * as elements, moved with the window at each redraw.
   */
  private readonly portals: { tile: { x: number; y: number }; mark: HTMLElement }[];

  constructor(private readonly options: MinimapOptions) {
    const { zone } = options;
    this.base = document.createElement('canvas');
    this.base.width = zone.width;
    this.base.height = zone.height;
    paintZone(this.base, options);

    this.root = document.createElement('section');
    this.root.className = 'minimap';
    this.title = document.createElement('h2');
    this.title.className = 'minimap__title';
    this.title.textContent = uiText(zone.name);
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'minimap__canvas';
    this.context = this.canvas.getContext('2d');
    this.dots = document.createElement('canvas');
    this.dots.className = 'minimap__dots';
    this.dotsContext = this.dots.getContext('2d');
    this.portals = zone.portals.map((portal) => ({
      tile: portal,
      mark: portalMark(portal.name),
    }));
    this.root.append(this.title, this.canvas, ...this.portals.map(({ mark }) => mark), this.dots);
    this.root.addEventListener('click', () => {
      this.large = !this.large;
      saveMinimapLarge(this.large);
      this.resize(isTouch());
    });
    document.body.append(this.root);
    this.offTouch = onTouchChange((touch) => {
      this.resize(touch);
    });
    this.unfollow = followPreference('minimap', (enabled) => {
      this.root.hidden = !enabled;
    });
  }

  /** The window's size for the current mode: the canvases at one pixel a tile, shown `scale` times larger. */
  private resize(touch: boolean): void {
    const { zone } = this.options;
    const layout = minimapLayout(this.large, touch);
    this.view = Math.min(layout.view, Math.max(zone.width, zone.height));
    for (const canvas of [this.canvas, this.dots]) {
      canvas.width = this.view;
      canvas.height = this.view;
    }
    this.canvas.style.width = `${String(this.view * layout.scale)}px`;
    this.canvas.style.height = `${String(this.view * layout.scale)}px`;
    this.root.classList.toggle('minimap--large', this.large);
    this.root.title = uiText(this.large ? 'Réduire la mini-carte' : 'Agrandir la mini-carte');
    this.draw(this.last.dots, this.last.markers);
  }

  /**
   * Where the player stands, in words: a village or a biome, and the levels
   * the place is meant for, so a player reads the danger before walking on.
   */
  setPlace(name: string, level?: { min: number; max: number }): void {
    this.title.textContent =
      level === undefined ? uiText(name) : `${uiText(name)} · ${levelLabel(level)}`;
  }

  /** `markers`: the townsfolk's quest marks (ADR 0047), drawn over the land and under the dots. */
  draw(dots: readonly MinimapDot[], markers: readonly MinimapMarker[] = []): void {
    this.last = { dots, markers };
    const ground = this.context;
    const context = this.dotsContext;
    if (ground === null || context === null) return;
    const { zone } = this.options;
    const self = dots.find((dot) => dot.kind === 'self');
    // The window follows the player, clamped so it never shows past the edge.
    const half = this.view / 2;
    const left = clamp((self?.x ?? zone.width / 2) - half, 0, zone.width - this.view);
    const top = clamp((self?.y ?? zone.height / 2) - half, 0, zone.height - this.view);
    ground.drawImage(this.base, left, top, this.view, this.view, 0, 0, this.view, this.view);
    paintQuestMarks(ground, markers, { x: left, y: top });
    context.clearRect(0, 0, this.view, this.view);
    for (const { tile, mark } of this.portals) {
      const x = tile.x - left;
      const y = tile.y - top;
      mark.hidden = x < 0 || y < 0 || x >= this.view || y >= this.view;
      mark.style.left = `${String(((x + 0.5) / this.view) * 100)}%`;
      mark.style.top = `${String(((y + 0.5) / this.view) * 100)}%`;
    }

    // Mobs first, then players, friends over them, the local player last: nothing hides "you".
    const order: MinimapDot['kind'][] = [
      'mob',
      'rare',
      'encounter',
      'player',
      'friend',
      'quest',
      'event',
      'self',
    ];
    for (const kind of order) {
      for (const dot of dots) {
        if (dot.kind !== kind) continue;
        const x = dot.x - left;
        const y = dot.y - top;
        if (kind === 'self') {
          context.fillStyle = '#000000';
          context.fillRect(x - 2, y - 2, 5, 5);
          context.fillStyle = COLOURS.self;
          context.fillRect(x - 1, y - 1, 3, 3);
          continue;
        }
        if (kind === 'quest') {
          // Out of the window, it waits on the rim, towards where to go.
          paintQuestTarget(
            context,
            Math.min(Math.max(x, 4), this.view - 5),
            Math.min(Math.max(y, 4), this.view - 5),
          );
          continue;
        }
        if (kind === 'event') {
          paintEventMark(
            context,
            Math.min(Math.max(x, 4), this.view - 5),
            Math.min(Math.max(y, 4), this.view - 5),
          );
          continue;
        }
        if (kind === 'encounter') {
          if (x < 0 || y < 0 || x >= this.view || y >= this.view) continue;
          context.globalAlpha = dot.faded === true ? ENCOUNTER_FADED_ALPHA : 1;
          paintEncounterMark(context, x, y);
          context.globalAlpha = 1;
          continue;
        }
        if (kind === 'rare') {
          if (x < 0 || y < 0 || x >= this.view || y >= this.view) continue;
          paintRareMark(context, x, y);
          continue;
        }
        if (kind === 'friend' || kind === 'player') {
          // A dark rim, so a player reads over a pictogram or a bright ground.
          context.fillStyle = PICTOGRAM_RIM;
          context.fillRect(x - 1, y - 1, 4, 4);
          context.fillStyle = COLOURS[kind];
          context.fillRect(x, y, 2, 2);
          continue;
        }
        context.fillStyle = COLOURS[kind];
        context.fillRect(x, y, 2, 2);
      }
    }
  }

  destroy(): void {
    this.unfollow();
    this.offTouch();
    this.root.remove();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.round(Math.min(Math.max(value, min), Math.max(min, max)));
}

function paintZone(canvas: HTMLCanvasElement, { zone, grid, field }: MinimapOptions): void {
  const context = canvas.getContext('2d');
  if (context === null) return;
  // Ground cover is too small to read at this scale: only what blocks is
  // drawn — houses in brown, stone in grey, the rest as woods.
  const blockers = new Map<string, string>();
  for (const placed of zone.props) {
    const definition = PROPS[placed.prop];
    if (definition?.blocks !== true) continue;
    const colour =
      definition.width > 1
        ? COLOURS.house
        : placed.prop === 'boulder' || placed.prop === 'barrel'
          ? COLOURS.rock
          : COLOURS.woods;
    for (let dy = 0; dy < definition.depth; dy += 1) {
      for (let dx = 0; dx < definition.width; dx += 1) {
        const x = placed.x - Math.floor((definition.width - 1) / 2) + dx;
        blockers.set(`${String(x)}:${String(placed.y - dy)}`, colour);
      }
    }
  }
  paintPixels(context, zone.width, zone.height, (x, y) => {
    if (exitAt(zone, { x, y }) !== undefined) return COLOURS.exit;
    const colour = terrainOf(field.charAt(x, y)).colour;
    return grid.isWalkable(x, y) ? colour : (blockers.get(`${String(x)}:${String(y)}`) ?? colour);
  });
  for (const spawn of zone.spawns) {
    const icon = MOBS[spawn.mob].mapIcon;
    // A quest giver's mark comes and goes with his quests: `draw` paints it.
    if (icon !== undefined && icon !== 'quest') paintPictogram(context, icon, spawn.x, spawn.y);
  }
  if (!zone.persistent) {
    for (const lair of zone.landmarks.filter((l) => l.kind === 'lair')) {
      paintPictogram(context, LAIR_PICTOGRAM, lair.x, lair.y - 1);
    }
  }
  const bastion = zone.landmarks.find((place) => place.name === COMPANY_QUARTER);
  if (bastion !== undefined) paintPictogram(context, COMPANY_PICTOGRAM, bastion.x, bastion.y);
}

/**
 * The townsfolk's quest marks (ADR 0047), over their heads so a keeper's own
 * pictogram stays readable under them, one pixel a tile: the minimap draws
 * them in its window (`offset`), the world map over the whole zone.
 */
export function paintQuestMarks(
  context: CanvasRenderingContext2D,
  markers: readonly MinimapMarker[],
  offset: { x: number; y: number } = { x: 0, y: 0 },
): void {
  for (const { x, y, marker } of markers) {
    paintPictogram(context, QUEST_PICTOGRAMS[marker], x - offset.x, y - offset.y - MARKER_LIFT);
  }
}

/**
 * Where a quest under way leads (ADR 0047): a golden diamond with a light
 * heart, as big as a townsperson's pictogram so it reads on the map (SHE-43).
 */
const QUEST_TARGET_PICTOGRAM = {
  rows: ['...#...', '..#o#..', '.#ooo#.', '#ooooo#', '.#ooo#.', '..#o#..', '...#...'],
  body: '#c88a1c',
  light: COLOURS.quest,
};

/** Where a quest under way leads: the golden diamond, centred on its tile. */
export function paintQuestTarget(context: CanvasRenderingContext2D, x: number, y: number): void {
  paintPictogram(context, QUEST_TARGET_PICTOGRAM, x, y);
}

/**
 * A world event's village (ADR 0050): two crossed blades in fire red, the
 * way a map marks a battle.
 */
const EVENT_PICTOGRAM = {
  rows: ['o.....o', '.o...o.', '..o.o..', '...#...', '..o.o..', '.#...#.', '#.....#'],
  body: '#8a1f1c',
  light: '#ff5c3c',
};

export function paintEventMark(context: CanvasRenderingContext2D, x: number, y: number): void {
  paintPictogram(context, EVENT_PICTOGRAM, x, y);
}

/**
 * A road encounter (ADR 0093): a small amber pennant on its pole, a stop
 * on the road worth the detour, smaller than an event's blades.
 */
const ENCOUNTER_PICTOGRAM = {
  rows: ['#oo', '#oo', '#..', '#..'],
  body: '#7a4a12',
  light: '#ffb347',
};

/** What a faded encounter keeps: there, but done for this player. */
const ENCOUNTER_FADED_ALPHA = 0.4;

export function paintEncounterMark(context: CanvasRenderingContext2D, x: number, y: number): void {
  paintPictogram(context, ENCOUNTER_PICTOGRAM, x, y);
}

/**
 * A wandering rare (ADR 0094): a small jade diamond with a bright heart, the
 * colour of its name and outline, over the red dots of its kin.
 */
const RARE_PICTOGRAM = {
  rows: ['..#..', '.#o#.', '#ooo#', '.#o#.', '..#..'],
  body: '#3ce8b4',
  light: '#d8fff0',
};

export function paintRareMark(context: CanvasRenderingContext2D, x: number, y: number): void {
  paintPictogram(context, RARE_PICTOGRAM, x, y);
}

/** A portal's arch, the favicon's (`.map-portal`, inventory.css), named on hover. */
export function portalMark(name: string): HTMLElement {
  const mark = document.createElement('span');
  mark.className = 'map-portal';
  mark.title = uiFormat('Portail de {name}', { name });
  return mark;
}

/** A pictogram centred on a tile: the dark rim first, one pixel all round, then the shape. */
function paintPictogram(
  context: CanvasRenderingContext2D,
  icon: MapIcon | { rows: readonly string[]; body: string; light: string },
  x: number,
  y: number,
): void {
  const { rows, body, light } = typeof icon === 'string' ? PICTOGRAMS[icon] : icon;
  const left = x - Math.floor((rows[0]?.length ?? 1) / 2);
  const top = y - Math.floor(rows.length / 2);
  const lit = (row: number, column: number): boolean => {
    const pixel = rows[row]?.[column];
    return pixel !== undefined && pixel !== '.';
  };
  context.fillStyle = PICTOGRAM_RIM;
  rows.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx += 1) {
      if (lit(dy, dx)) context.fillRect(left + dx - 1, top + dy - 1, 3, 3);
    }
  });
  rows.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx += 1) {
      if (!lit(dy, dx)) continue;
      context.fillStyle = row[dx] === 'o' ? light : body;
      context.fillRect(left + dx, top + dy, 1, 1);
    }
  });
}

/** « niv. 5-7 », in the player's language: a hyphen, the interface font has no en dash. */
export function levelLabel(level: { min: number; max: number }): string {
  return level.min === level.max
    ? uiFormat('niv. {level}', { level: level.min })
    : uiFormat('niv. {min}-{max}', { min: level.min, max: level.max });
}
